import { z } from 'zod';
import { BUILDING_TYPES, normalizePhone } from './crm';
import './schemas'; // polskie komunikaty zod

/**
 * Sprzedaż w terenie (docs/20): handlowiec / koordynator u klienta — szybka wycena (cena i termin, nic nie
 * zapisuje), po zgodzie klienta wstępna rezerwacja terminu, sprawa i oferta z wyceny, podpis na ekranie,
 * zlecenie z zaplanowanym montażem i płatność (na miejscu kartą / odroczona; zaliczka / całość).
 */

const uuid = z.string().uuid();
const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data w formacie RRRR-MM-DD');
const optStr = (max: number) => z.string().trim().max(max).default('');
const buildingType = z.enum(Object.keys(BUILDING_TYPES) as [keyof typeof BUILDING_TYPES, ...(keyof typeof BUILDING_TYPES)[]]);

/** Domyślna długość montażu (dni robocze) i najdłuższa możliwa w wycenie. */
export const FIELD_SALE_DEFAULT_DAYS = 1;
export const FIELD_SALE_MAX_DAYS = 10;
/** Ile godzin trwa wstępna rezerwacja terminu bez podpisu. */
export const FIELD_SALE_HOLD_HOURS = 2;

export const FIELD_PAYMENT_WHEN = { na_miejscu: 'Na miejscu (karta)', odroczona: 'Odroczona (przelew)' } as const;
export type FieldPaymentWhen = keyof typeof FIELD_PAYMENT_WHEN;
export const FIELD_PAYMENT_AMOUNT = { zaliczka: 'Zaliczka', calosc: 'Całość' } as const;
export type FieldPaymentAmount = keyof typeof FIELD_PAYMENT_AMOUNT;

/**
 * Pozycja wyceny: z katalogu (cena z cennika albo zmieniona przez handlowca) albo ręczna (nazwa, jednostka, cena).
 * Ceny podawane brutto za jednostkę — tak rozmawia się z klientem; serwer przelicza na netto według stawki VAT oferty.
 */
export const fieldQuoteLineSchema = z
  .object({
    catalogItemId: uuid.nullable().default(null),
    name: z.string().trim().max(300).default(''),
    unit: z.string().trim().min(1).max(10).default('szt.'),
    quantity: z.number().positive('Ilość musi być większa od zera').max(10_000),
    /** cena jednostkowa brutto ustalona przez handlowca (null — z cennika) */
    unitGrossGr: z.number().int().min(0, 'Cena nie może być ujemna').max(100_000_000_00).nullable().default(null),
  })
  .refine((l) => !!l.catalogItemId || (l.name.length >= 2 && l.unitGrossGr !== null), 'Pozycja ręczna wymaga nazwy (min. 2 znaki) i ceny');
export type FieldQuoteLineInput = z.infer<typeof fieldQuoteLineSchema>;

export const fieldQuoteSchema = z.object({
  tradeCode: z.string().trim().min(2).max(4),
  lines: z
    .array(fieldQuoteLineSchema)
    .min(1, 'Dodaj co najmniej jedną pozycję')
    .max(50),
  /** rabat na całość (procent) */
  discountPct: z.number().min(0).max(100).default(0),
  days: z.number().int().min(1).max(FIELD_SALE_MAX_DAYS).default(FIELD_SALE_DEFAULT_DAYS),
  /** rodzaj budynku — stawka VAT (8% dom / mieszkanie, inaczej 23%) jak w ofercie z biura */
  buildingType: buildingType.default('dom_jednorodzinny'),
  /** szukaj terminów od tego dnia (domyślnie jutro) */
  from: day.nullable().default(null),
});
export type FieldQuoteInput = z.infer<typeof fieldQuoteSchema>;

export const fieldSlotSchema = z.object({ teamId: uuid, dateFrom: day, dateTo: day });
export type FieldSlotInput = z.infer<typeof fieldSlotSchema>;

/** Zgoda klienta na cenę i termin: zapis sprzedaży (id z telefonu) i wstępna rezerwacja terminu. */
export const fieldSaleStartSchema = z.object({
  id: uuid,
  quote: fieldQuoteSchema,
  /** null — bez terminu (handlowiec bez roli koordynatora albo sprzedaż powyżej progu umowy) */
  slot: fieldSlotSchema.nullable().default(null),
  /** sprzedaż rozpoczęta z wpisu „Mojego kalendarza” bez sprawy — sprawa ze sprzedaży podepnie się pod ten wpis (docs/22) */
  ownTaskId: uuid.nullable().default(null),
});
export type FieldSaleStartInput = z.infer<typeof fieldSaleStartSchema>;

/** Dane klienta i adres montażu — z nich powstaje sprawa (opiekun: sprzedający). */
export const fieldSaleClientSchema = z
  .object({
    firstName: optStr(80),
    lastName: z.string().trim().min(2, 'Podaj nazwisko').max(80),
    phone: z
      .string()
      .trim()
      .max(30)
      .refine((v) => v === '' || normalizePhone(v) !== null, 'Numer telefonu musi mieć co najmniej 9 cyfr')
      .default(''),
    email: z.union([z.literal(''), z.string().trim().toLowerCase().email('Niepoprawny adres e-mail')]).default(''),
    street: optStr(120),
    postalCode: z
      .string()
      .trim()
      .refine((v) => v === '' || /^\d{2}-\d{3}$/.test(v), 'Kod pocztowy w formacie 00-000')
      .default(''),
    city: z.string().trim().min(2, 'Podaj miejscowość').max(80),
    description: optStr(4000),
  })
  .superRefine((v, ctx) => {
    if (!v.phone && !v.email) ctx.addIssue({ code: 'custom', path: ['phone'], message: 'Podaj telefon albo e-mail klienta' });
  });
export type FieldSaleClientInput = z.infer<typeof fieldSaleClientSchema>;

/** Podpis klienta na ekranie i wybór płatności — razem, bo zlecenie z harmonogramem powstaje z chwilą akceptacji. */
export const fieldSaleSignSchema = z.object({
  personName: z.string().trim().min(3, 'Podaj imię i nazwisko podpisującego').max(120),
  email: z.union([z.literal(''), z.string().trim().toLowerCase().email('Niepoprawny adres e-mail')]).default(''),
  /** żądanie rozpoczęcia prac przed upływem 14 dni na odstąpienie (umowa poza lokalem przedsiębiorstwa) */
  earlyStart: z.boolean().default(false),
  signature: z.object({ strokes: z.array(z.array(z.tuple([z.number(), z.number(), z.number()])).max(5000)).min(1, 'Brak podpisu').max(200) }),
  paymentWhen: z.enum(Object.keys(FIELD_PAYMENT_WHEN) as [FieldPaymentWhen, ...FieldPaymentWhen[]]),
  paymentAmount: z.enum(Object.keys(FIELD_PAYMENT_AMOUNT) as [FieldPaymentAmount, ...FieldPaymentAmount[]]),
});
export type FieldSaleSignInput = z.infer<typeof fieldSaleSignSchema>;

/** Powyżej progu umowy: akceptacja oferty podpisem klienta na ekranie (bez płatności i terminu — umowę przygotuje biuro). */
export const fieldSaleAcceptSchema = fieldSaleSignSchema.pick({ personName: true, email: true, signature: true });
export type FieldSaleAcceptInput = z.infer<typeof fieldSaleAcceptSchema>;

// ── DTO ────────────────────────────────────────────────────────────────

export interface FieldCatalogItemDto {
  id: string;
  name: string;
  code: string;
  kind: string;
  unit: string;
  vatRate: number;
  /** cena jednostkowa netto i brutto (z katalogu, przed rabatem) */
  netGr: number;
  grossGr: number;
}

export interface FieldSlotDto {
  teamId: string;
  teamName: string;
  dateFrom: string;
  dateTo: string;
}

export interface FieldQuoteDto {
  tradeCode: string;
  tradeName: string;
  companyName: string;
  lines: {
    catalogItemId: string | null;
    name: string;
    unit: string;
    quantity: number;
    vatRate: number;
    /** cena jednostkowa brutto (ustalona albo z cennika) i z cennika (null — pozycja ręczna) */
    unitGrossGr: number;
    listGrossGr: number | null;
    grossGr: number;
  }[];
  discountPct: number;
  buildingType: string;
  vatProfile: string;
  /** rabat ponad limit firmy — oferta wymaga zatwierdzenia (osoba z uprawnieniem) */
  overDiscountLimit: boolean;
  maxDiscountPct: number;
  totals: { netGr: number; vatGr: number; grossGr: number };
  depositPct: number;
  depositGr: number;
  thresholdGr: number;
  /** suma ≥ progu umowy — sprzedaż na miejscu niemożliwa (umowa w biurze) */
  overThreshold: boolean;
  days: number;
  /** wolne terminy (null — użytkownik nie planuje prac: tylko koordynator widzi ekipy i kalendarze) */
  slots: FieldSlotDto[] | null;
}

export type FieldSaleStatus = 'rezerwacja' | 'oferta' | 'podpisana' | 'zakonczona' | 'biuro' | 'porzucona';

export interface FieldSaleDto {
  id: string;
  status: FieldSaleStatus;
  tradeCode: string;
  quote: FieldQuoteDto | null;
  slot: FieldSlotDto | null;
  holdUntil: string | null;
  caseId: string | null;
  caseNumber: string | null;
  offerNumber: string | null;
  /** wydana | czeka na zatwierdzenie (rabat ponad próg) | robocza */
  offerState: 'wydana' | 'czeka_na_zatwierdzenie' | 'robocza' | null;
  orderNumber: string | null;
  taskNumber: string | null;
  /** zaplanowany montaż (po podpisie) */
  term: { dateFrom: string; dateTo: string; teamName: string } | null;
  payment: {
    when: FieldPaymentWhen;
    amount: FieldPaymentAmount;
    /** kwota do pobrania na miejscu (0 przy odroczonej) */
    dueNowGr: number;
    paid: boolean;
    /** płatność na miejscu przez ING Pay (firma zlecenia ma aktywną integrację) — bez ręcznego „Płatność rozliczona” */
    online: boolean;
    /** klient zapłaci później — wysłano link „Zapłać online” (e-mail) */
    later: boolean;
  } | null;
  message: string;
}

// ── kalendarz ekip w aplikacji (koordynator, podgląd) ─────────────────

/** Stan dnia ekipy: praca, nieobecność kogoś ze składu, wstępna rezerwacja ze sprzedaży, wolny albo bez składu. */
export type CrewDayState = 'zajety' | 'nieobecnosc' | 'rezerwacja' | 'wolny' | 'brak_skladu';

export interface CrewDayDto {
  day: string;
  state: CrewDayState;
  items: { taskId: string; number: string; title: string; client: string | null; city: string | null; timeFrom: string | null; timeTo: string | null }[];
  members: string[];
  absent: { name: string; label: string }[];
  hold: { until: string; byName: string } | null;
}

export interface CrewWeekDto {
  tradeCode: string;
  from: string;
  to: string;
  days: string[];
  teams: { teamId: string; name: string; color: string; days: CrewDayDto[] }[];
}

/** Płatność na miejscu przez ING Pay: adres bramki (kod QR dla klienta / „Otwórz tutaj”). */
export interface FieldOnlinePaymentDto {
  onlinePaymentId: string;
  /** strona bramki ING Pay */
  url: string;
  /** kod QR adresu bramki (PNG, data URL) */
  qr: string;
  amountGr: number;
}

/** Stan płatności na miejscu: new / pending / settled / rejected / cancelled / error (statusy ING Pay). */
export interface FieldOnlinePaymentStateDto {
  status: string;
  sale: FieldSaleDto;
}
